/**
 * User Menu Component
 * Phase 6: Frontend Application
 *
 * Displays user avatar/name and provides login/logout actions.
 */

'use client';

import { isAuthConfigured, useAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { ChevronDown, LogIn, LogOut, Settings, User as UserIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * User menu with login/logout functionality
 */
export function UserMenu(): JSX.Element {
  const { isAuthenticated, isLoading, user, login, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auth not configured - show disabled state
  if (!isAuthConfigured()) {
    return (
      <div className="text-sm text-gray-500">
        Auth not configured
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
    );
  }

  // Not authenticated - show login button
  if (!isAuthenticated) {
    return (
      <button
        onClick={() => login().catch((e) => logger.error('Login failed', e))}
        className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
      >
        <LogIn className="h-4 w-4" />
        Sign In
      </button>
    );
  }

  // Authenticated - show user icon with dropdown
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 rounded-full p-1 hover:bg-gray-100 transition-colors"
        aria-label="User menu"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <UserIcon className="h-5 w-5" />
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg z-50">
          {/* User info */}
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email}
            </p>
            {user?.roles && user.roles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {user.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700"
                  >
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Menu items */}
          <div className="py-1">
            <a
              href="/profile"
              onClick={() => setIsOpen(false)}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Settings className="h-4 w-4" />
              Profile Settings
            </a>
            <button
              onClick={() => {
                setIsOpen(false);
                logout().catch((e) => logger.error('Logout failed', e));
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
